/**
 * studentAccountViewModel — ce que « Mon compte » (`<StudentAccountPage>`) lit dans le store.
 *
 * Patron A (adossé au store) : le compte connecté (`currentUser.object`), que `getUser` et
 * `getSkillSets` rafraîchissent (ce dernier pose `skill_sets`), et deux données statiques — le fuseau
 * du serveur et les compétences (le total d'une étape de quête). Chaque bloc est mis en forme par
 * `studentAccountAdapter` ; l'écran ne fait que poser les gestes.
 *
 * L'avatar « travaille » pendant son envoi (`updateAvatarSelected`) et pendant une génération par
 * IA lancée ailleurs (`currentUser.avatarGenerating`) : `CustomizableAvatar.js` montrait les deux.
 */
import moment from "moment"
import { createSelector } from "reselect"

import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"

import { canReadSubject } from "../../../adapters/student/shared/studentOfferAdapter"
import {
    toStudentAccountGamification,
    toStudentAccountIdentity,
    toStudentAccountTodos,
    toStudentActivityStats,
    toStudentTitleOptions,
    toStudentTitleProgress,
    type ApiSkill,
    type ApiStudentAccountUser,
} from "../../../adapters/student/Account/studentAccountAdapter"
import { type UIActivityStatsPeriod } from "../../../../../design-system/organisms/ActivityStatsCard"
import {
    type UIStudentAccountGamification,
    type UIStudentAccountIdentity,
    type UIStudentTitleOption,
    type UIStudentTitleProgress,
} from "../../../ui/types/studentAccount"
import { selectAvatarGeneration, selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentAccountViewModel = {
    /** `null` tant que le compte n'est pas là. */
    identity: UIStudentAccountIdentity | null
    loading: boolean
    gamification: UIStudentAccountGamification | null
    titleProgress: UIStudentTitleProgress | null
    titleOptions: UIStudentTitleOption[]
    /** La quête dont l'élève porte le titre. */
    selectedTitleId: string | number | null
    /** Quêtes, classements, gestes : sous `my_successes`, comme l'ancien écran. */
    canOpenGame: boolean
    statistics: UIActivityStatsPeriod[] | null
    initialStatisticsPeriodKey?: string
    needsEmailValidation: boolean
    canCompleteProfile: boolean
    profileCompletion: number | null
    avatarBusy: boolean
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectUser = (state: ReduxState): ApiStudentAccountUser | undefined => selectDisplayedStudent(state)
const selectServerTimezone = (state: ReduxState): string | undefined => state?.data?.staticData?.server_timezone
const selectSkills = (state: ReduxState): ApiSkill[] | undefined => state?.data?.staticData?.skills
const selectAvatarSending = (state: ReduxState): boolean => !!state?.api?.updateAvatarSelected?.request
const selectAvatarGenerating = (state: ReduxState): boolean => !!selectAvatarGeneration(state)?.isGenerating

export const studentAccountViewModel = createSelector(
    [selectUser, selectServerTimezone, selectSkills, selectAvatarSending, selectAvatarGenerating],
    (user, serverTimezone, skills, avatarSending, avatarGenerating): StudentAccountViewModel => {
        const identity = toStudentAccountIdentity(user, serverTimezone)
        const stats = toStudentActivityStats(user?.statistics, moment())
        const todos = toStudentAccountTodos(user)
        // The space's game block off: no counters, title or rank here either, like on Home.
        const gameOn = isBlockOn(user, TENANT_BLOCKS.GAMIFICATION)

        return {
            identity,
            loading: identity == null,
            gamification: gameOn ? toStudentAccountGamification(user) : null,
            titleProgress: gameOn ? toStudentTitleProgress(user, skills) : null,
            titleOptions: gameOn ? toStudentTitleOptions(user?.skill_sets, skills) : [],
            selectedTitleId: user?.title_skill_set_id ?? null,
            canOpenGame: canReadSubject(user?.roles, "my_successes") && gameOn,
            statistics: canReadSubject(user?.roles, "my_successes") ? (stats?.periods ?? null) : null,
            initialStatisticsPeriodKey: stats?.initialPeriodKey,
            ...todos,
            avatarBusy: avatarSending || avatarGenerating,
        }
    }
)
